// Service Worker registration (Story 7.2). Registration is environment-gated: it runs only in
// production builds, or in dev when VITE_PWA_DEV=true, so a plain `npm run dev` session never
// installs the SW and stale-cache surprises don't bite local iteration.
//
// Plain `navigator.serviceWorker` is used deliberately instead of `virtual:pwa-register` — the
// virtual module only resolves through the Vite/PWA build pipeline and would break the Vitest
// suite, and keeping a pure predicate makes the gate trivially unit-testable.

/**
 * Pure predicate: should the Service Worker register in this environment?
 * Mirrors the epic-pinned gate `import.meta.env.PROD || import.meta.env.VITE_PWA_DEV === "true"`.
 * The matching `devOptions.enabled` toggle in vite.config.ts reads the same VITE_PWA_DEV flag so
 * the dev SW actually compiles + serves when the gate is on.
 */
export function shouldRegisterServiceWorker(env: ImportMetaEnv): boolean {
  return env.PROD || env.VITE_PWA_DEV === "true";
}

/**
 * Register the Service Worker when the gate passes and the browser supports it. Failures are
 * logged and swallowed — a missing/failed SW must never block the app from rendering.
 *
 * Always `async`: every path (both early exits and the happy path) resolves a single
 * `Promise<void>`, so callers and tests can await completion deterministically without a
 * `?? Promise.resolve()` dance, and it never rejects.
 *
 * `updateViaCache: "none"` forces the browser to always revalidate `/sw.js` over the network
 * rather than serving it from the HTTP cache, so a bad SW script can't wedge controlled clients
 * on a stale worker after a deploy.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!shouldRegisterServiceWorker(import.meta.env)) return;
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
      updateViaCache: "none",
    });
    trackUpdates(reg);
  } catch (e) {
    console.error("[register-sw] registration failed:", e);
  }
}

// --- Update-available signal (Story 7.5 / #420) ------------------------------
// The SW deliberately does NOT skipWaiting() on install (see sw.ts lifecycle), so after a deploy
// the new worker sits in the "waiting" state while the old one keeps controlling open tabs — a
// plain F5 then shows the STALE app shell (the #420 repro: the old /profile placeholder instead of
// the Story 16.3 certificate card). This module derives an OPT-IN "update available" signal the
// app surfaces as a dismissible banner; the new worker only takes over when the clinician clicks
// Reload (never a mid-session auto-swap). The detection deliberately distinguishes a genuine UPDATE
// (a new worker installed WHILE a previous one controls the page) from the FIRST-EVER install (no
// controller yet) — the latter must NOT prompt.

export interface ServiceWorkerUpdateState {
  /** True once a NEW worker has installed and is waiting (an update is ready to apply). */
  updateAvailable: boolean;
  /** The waiting worker to postMessage when the user opts in (or null before one exists). */
  waiting: ServiceWorker | null;
}

let updateState: ServiceWorkerUpdateState = { updateAvailable: false, waiting: null };
const updateListeners = new Set<(state: ServiceWorkerUpdateState) => void>();

/** Current update-available snapshot (the banner reads this on mount). */
export function getServiceWorkerUpdateState(): ServiceWorkerUpdateState {
  return updateState;
}

/** Subscribe to update-available changes. Returns an unsubscribe function. */
export function subscribeServiceWorkerUpdate(
  listener: (state: ServiceWorkerUpdateState) => void,
): () => void {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

function emitUpdateAvailable(waiting: ServiceWorker | null): void {
  updateState = { updateAvailable: true, waiting };
  updateListeners.forEach((listener) => listener(updateState));
}

/**
 * Wire update detection onto a registration (AC20). Exported for unit testing. THREE sources, so no
 * matter where the update lands relative to when this runs the banner still surfaces:
 *   (a) ``reg.waiting`` already present at load AND a controller exists → an update is ready now
 *       (a prior visit installed it; this tab booted on the old worker).
 *   (b) ``reg.installing`` already present at load → the F5-after-deploy timing (#420): register()
 *       triggers the update check and ``updatefound`` fires — setting reg.installing — DURING the
 *       register() microtask, i.e. before this listener attaches. reg.waiting is still null (a
 *       misses) and the event has already passed (c misses), so the installing worker must be
 *       observed DIRECTLY or the signal is lost and no banner ever shows.
 *   (c) ``updatefound`` after load → a new worker starts installing later in the session.
 * (b) and (c) share the same statechange handler: on ``state === "installed"`` with a controller
 * present it is an UPDATE → signal; with NO controller it is the FIRST-EVER install → MUST NOT
 * signal (no prompt on first install — AC20).
 * Defensive against partial mocks / a missing registration (the catch in registerServiceWorker
 * would otherwise swallow a thrown ``addEventListener`` on a bare mock).
 */
export function trackUpdates(reg: ServiceWorkerRegistration | undefined): void {
  if (!reg || typeof reg.addEventListener !== "function") return;
  const hasController = (): boolean =>
    "serviceWorker" in navigator && navigator.serviceWorker.controller != null;

  // Signal once an installing worker reaches "installed" AND a prior worker controlled the page.
  // Attach at most once per worker: branch (b) and branch (c) can both reach the SAME installing
  // worker (updatefound may fire after we already picked it up via reg.installing), and a double
  // statechange listener would emit twice.
  const watched = new WeakSet<ServiceWorker>();
  const watchInstalling = (worker: ServiceWorker): void => {
    if (watched.has(worker)) return;
    watched.add(worker);
    worker.addEventListener("statechange", () => {
      if (worker.state !== "installed") return;
      if (hasController()) emitUpdateAvailable(reg.waiting ?? worker);
    });
  };

  if (reg.waiting && hasController()) {
    emitUpdateAvailable(reg.waiting);
  }

  // (b) A worker mid-install when we start tracking — updatefound may already have fired, so pick it
  // up directly rather than waiting for an event that has already passed.
  if (reg.installing) watchInstalling(reg.installing);

  // (c) A worker that starts installing later this session.
  reg.addEventListener("updatefound", () => {
    if (reg.installing) watchInstalling(reg.installing);
  });
}

// Reload-once guard (AC21): a single controllerchange listener is armed on the first opt-in, and a
// flag stops a double reload if the event fires more than once.
let reloadArmed = false;
let reloaded = false;

/**
 * Apply a pending update (AC21): post ``{type:"franklin-skip-waiting"}`` to the waiting worker so
 * it leaves the waiting phase, and reload EXACTLY ONCE on the next ``controllerchange`` (when the
 * new worker takes control). Never auto-reloads on its own — only in response to this explicit,
 * user-initiated call. Idempotent: arms the listener once, reloads once.
 */
export function applyServiceWorkerUpdate(waiting: ServiceWorker | null): void {
  // Nothing waiting to activate — don't arm the reload listener with no skip-waiting to post.
  if (!waiting) return;
  if ("serviceWorker" in navigator && !reloadArmed) {
    reloadArmed = true;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  }
  waiting.postMessage({ type: "franklin-skip-waiting" });
}
